import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../services/api";
import { API_BASE_URL } from "../utils/constants";
import toast from "react-hot-toast";
import { FiRefreshCw } from "react-icons/fi";
import PaymentModeDropdown from "../components/common/PaymentModeDropdown";
import {
  HiCube,
  HiExclamation,
  HiCash,
  HiDocumentText,
  HiArrowNarrowRight,
} from "react-icons/hi";
import AppNavbar from "../components/Navbar";
import Loading from "../components/Loading";
import "../styles/global.css";
import "./PaymentStatus.css";

const PaymentStatus = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshingOrderId, setRefreshingOrderId] = useState(null);
  const [paymentProviderByOrderId, setPaymentProviderByOrderId] = useState({});

  const getPaymentProvider = (orderId) =>
    paymentProviderByOrderId[orderId] || "razorpay";

  useEffect(() => {
    fetchOrders();
  }, []);

  // Handle payment success redirect
  useEffect(() => {
    const orderId = searchParams.get("orderId");
    const status = searchParams.get("status");
    const razorpayPaymentLinkId = searchParams.get("razorpay_payment_link_id");
    const razorpayPaymentId = searchParams.get("razorpay_payment_id");
    const stripeSessionId = searchParams.get("session_id");

    // If we returned from a gateway to the frontend (common in localhost dev),
    // bounce the browser to the backend success endpoint so it can:
    // 1) verify payment (Razorpay link / Stripe session)
    // 2) mark WHMCS invoice paid (AddInvoicePayment)
    // 3) AcceptOrder
    // 4) provision resource + redirect user to the right page
    if (
      orderId &&
      status === "success" &&
      (razorpayPaymentLinkId || stripeSessionId)
    ) {
      const base = (API_BASE_URL || "").toString().replace(/\/$/, "");
      if (!base) {
        // Shouldn't happen if env is configured, but fail gracefully.
        toast.error("Payment callback misconfigured. Missing API base URL.");
        return;
      }

      const params = new URLSearchParams();
      params.set("orderId", String(orderId));
      if (razorpayPaymentLinkId) {
        params.set("razorpay_payment_link_id", String(razorpayPaymentLinkId));
      }
      if (razorpayPaymentId) {
        params.set("razorpay_payment_id", String(razorpayPaymentId));
      }
      if (stripeSessionId) {
        params.set("session_id", String(stripeSessionId));
      }

      window.location.href = `${base}/payments/success?${params.toString()}`;
      return;
    }

    if (orderId && status === "paid") {
      // Payment was successful, check order status and redirect
      const checkAndRedirect = async () => {
        try {
          const response = await api.get(`/whmcs/order/${orderId}`);
          const order = response.data;

          if (order.status === "activated") {
            // Check if order has a droplet database ID - this is set when activated
            if (order.dropletDbId) {
              // Use database ID for navigation
              navigate(`/server/${order.dropletDbId}?provisioning=true`);
              return;
            } else if (order.resourceId) {
              // Check resource type from config
              const config = order.config || {};

              // Check if it's a space
              if (config.storage_tier || config.acl) {
                const targetSpaceId = order.resourceId || config.name;
                if (targetSpaceId) {
                  navigate(`/spaces/${targetSpaceId}?provisioning=true`);
                  return;
                }
              }

              // Check if it's a volume - redirect to the droplet page
              if (
                config.sizeGigabytes &&
                (config.dropletId || config.droplet_id)
              ) {
                const dropletId = config.dropletId || config.droplet_id;
                if (dropletId) {
                  navigate(`/server/${dropletId}?provisioning=true`);
                  return;
                }
              }

              // Other resource types - redirect to orders page
              navigate(`/orders?orderId=${orderId}&status=activated`);
              return;
            }
          }

          // If not activated yet, refresh orders to show updated status
          fetchOrders(true);
          toast.success("Payment successful! Your order is being processed.");
        } catch (error) {
          console.error("Error checking order status:", error);
          fetchOrders(true);
        }
      };

      checkAndRedirect();
    }
  }, [searchParams, navigate]);

  const fetchOrders = async (showToast = false) => {
    try {
      const response = await api.get("/whmcs/orders");
      const newOrders = response.data.orders || [];
      setOrders(newOrders);
      if (showToast) {
        toast.success("Orders refreshed");
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      setOrders([]);
      if (showToast) {
        toast.error("Failed to refresh orders");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefreshAll = async () => {
    setRefreshing(true);
    await fetchOrders(true);
  };

  const handleRefreshOrder = async (orderId) => {
    setRefreshingOrderId(orderId);
    try {
      // Get the current status before refresh
      const currentOrder = orders.find((o) => o.id === orderId);
      const previousStatus = currentOrder?.status;

      // Force-check invoice status from WHMCS for this specific order
      const orderRes = await api.get(`/whmcs/order/${orderId}`);
      const updatedOrder = orderRes.data;

      // Refresh list after the check (so UI is consistent)
      const response = await api.get("/whmcs/orders");
      const updatedOrders = response.data.orders || [];
      setOrders(updatedOrders);

      if (updatedOrder) {
        // Only show status change messages if status actually changed
        if (previousStatus && previousStatus !== updatedOrder.status) {
          // Check if it's a Spaces order
          const isSpacesOrder =
            updatedOrder.config?.storage_tier || updatedOrder.config?.acl;

          const statusMessages = {
            paid: "Invoice payment confirmed!",
            activated: isSpacesOrder
              ? "Order activated! Space is being provisioned."
              : "Order activated! Droplet is being created.",
            failed: "Order activation failed.",
          };
          const message =
            statusMessages[updatedOrder.status] || "Order status updated";
          toast.success(message);
        } else {
          // For orders that haven't changed status, just confirm refresh
          toast.success("Order status checked");
        }
      }
    } catch (error) {
      console.error("Error refreshing order:", error);
      toast.error("Failed to refresh order status");
    } finally {
      setRefreshingOrderId(null);
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "paid":
      case "activated":
        return "status-badge activated";
      case "pending":
        return "status-badge pending";
      case "failed":
        return "status-badge failed";
      case "destroyed":
        return "status-badge destroyed";
      default:
        return "status-badge";
    }
  };

  const getStatusDisplay = (status) => {
    switch (status) {
      case "activated":
        return "Active";
      case "paid":
        return "Paid";
      case "pending":
        return "Pending";
      case "failed":
        return "Failed";
      case "destroyed":
        return "Destroyed";
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  if (loading) {
    return (
      <>
        <AppNavbar />
        <div className="container page-container-padding">
          <Loading text="Loading orders..." />
        </div>
      </>
    );
  }

  return (
    <>
      <AppNavbar />
      <div className="container page-container-padding">
        <div className="page-header main-container">
          <div>
            <h1 className="page-title">Order History</h1>
            <p className="page-subtitle">
              View and manage your orders and payments
            </p>
          </div>
          <button
            className="btn-refresh"
            onClick={handleRefreshAll}
            disabled={refreshing || loading}
          >
            <FiRefreshCw
              className={`btn-refresh-icon ${refreshing ? "spinning" : ""}`}
            />
            {refreshing ? "Refreshing..." : "Refresh All"}
          </button>
        </div>

        {orders.length === 0 ? (
          <div className="empty-state-card">
            <div className="empty-icon">
              <HiCube />
            </div>
            <h3 className="empty-title">No orders found</h3>
            <p className="empty-desc">
              You haven't placed any orders yet. Create your first server order
              to get started.
            </p>
            <div className="empty-actions">
              <button
                className="btn-primary"
                onClick={() => navigate("/order")}
              >
                Create New Order
              </button>
            </div>
          </div>
        ) : (
          <div className="orders-grid">
            {orders.map((order) => {
              const isSpacesOrder =
                Boolean(order?.config?.storage_tier) ||
                Boolean(order?.config?.acl);

              return (
                <div key={order.id} className="order-card">
                  {/* Header */}
                  <div className="order-header-row">
                    <div className="order-header-left">
                      <h3 className="order-title">Order #{order.id}</h3>
                      <div className="order-meta-row">
                        {order.invoiceNumber && (
                          <p className="order-meta">
                            Invoice #{order.invoiceNumber}
                          </p>
                        )}
                        <p className="order-meta">
                          {new Date(order.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="order-actions-header">
                      <button
                        className="btn-refresh btn-refresh-small"
                        onClick={() => handleRefreshOrder(order.id)}
                        disabled={refreshingOrderId === order.id}
                        title="Check order status"
                      >
                        <FiRefreshCw
                          className={`btn-refresh-icon ${
                            refreshingOrderId === order.id ? "spinning" : ""
                          }`}
                        />
                      </button>
                      <span className={getStatusClass(order.status)}>
                        {getStatusDisplay(order.status)}
                      </span>
                    </div>
                  </div>

                  {/* Main Content Grid */}
                  <div className="order-content-grid">
                    {/* Configuration */}
                    {order.config && (
                      <div className="order-config-section">
                        <p className="order-config-label">Configuration</p>
                        <div className="order-config-details">
                          <p>
                            <strong>Name:</strong> {order.config.name}
                          </p>
                          <p>
                            <strong>Size:</strong>{" "}
                            {order.config.size ??
                              (order.config.sizeGigabytes != null
                                ? `${order.config.sizeGigabytes} GB`
                                : order.config.size_gigabytes != null
                                ? `${order.config.size_gigabytes} GB`
                                : order.config.sizeGb != null
                                ? `${order.config.sizeGb} GB`
                                : order.config.storage_tier ||
                                  order.config.capacity ||
                                  "N/A")}
                          </p>
                          <p>
                            <strong>Region:</strong> {order.config.region}
                          </p>
                          {order.config.backups && (
                            <p>
                              <strong>Backups:</strong> Enabled
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Billing Period */}
                    {(order.billingStartDate || order.billingEndDate) && (
                      <div className="order-config-section">
                        <p className="order-config-label">Billing Period</p>
                        <div className="order-config-details">
                          <p>
                            <strong>Period:</strong>{" "}
                            {order.billingStartDate
                              ? new Date(
                                  order.billingStartDate
                                ).toLocaleDateString()
                              : "N/A"}{" "}
                            -{" "}
                            {order.billingEndDate
                              ? new Date(
                                  order.billingEndDate
                                ).toLocaleDateString()
                              : "N/A"}
                          </p>
                          {order.nextRenewalDate &&
                            order.status === "activated" && (
                              <p>
                                <strong>Next Renewal:</strong>{" "}
                                {new Date(
                                  order.nextRenewalDate
                                ).toLocaleDateString()}
                              </p>
                            )}
                          {order.usageDays !== null &&
                            order.usageDays !== undefined && (
                              <p>
                                <strong>Usage:</strong> {order.usageDays}
                                {order.billingStartDate &&
                                  order.billingEndDate && (
                                    <span>
                                      /
                                      {Math.ceil(
                                        (new Date(order.billingEndDate) -
                                          new Date(order.billingStartDate)) /
                                          (1000 * 60 * 60 * 24)
                                      )}{" "}
                                      days
                                    </span>
                                  )}
                              </p>
                            )}
                        </div>
                      </div>
                    )}

                    {/* Pricing */}
                    {order.total != null && order.total !== 0 && (
                      <div className="order-config-section">
                        <p className="order-config-label">Pricing</p>
                        <div className="order-config-details">
                          <p className="order-price-large">
                            ₹
                            {typeof order.total === "number"
                              ? order.total.toFixed(2)
                              : parseFloat(order.total || 0).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Alerts Section (Refund, Suspension, Renewal) */}
                  {(order.refundAmount ||
                    order.suspensionInfo?.suspended ||
                    order.renewalInvoiceId) && (
                    <div className="order-alerts">
                      {/* Refund Alert */}
                      {order.refundAmount !== null &&
                        order.refundAmount !== undefined &&
                        parseFloat(order.refundAmount) > 0 && (
                          <div className="order-alert order-alert-refund">
                            <span className="order-alert-icon">
                              <HiCash />
                            </span>
                            <div className="order-alert-content">
                              <p className="order-alert-title">
                                Refund Processed: ₹
                                {typeof order.refundAmount === "number"
                                  ? order.refundAmount.toFixed(2)
                                  : parseFloat(order.refundAmount || 0).toFixed(
                                      2
                                    )}
                              </p>
                              <p className="order-alert-desc">
                                Credit has been added to your WHMCS account
                                {order.refundCreditId &&
                                  ` (ID: ${order.refundCreditId})`}
                              </p>
                            </div>
                          </div>
                        )}

                      {/* Suspension Alert */}
                      {order.suspensionInfo &&
                        order.suspensionInfo.suspended && (
                          <div
                            className={`order-alert ${
                              order.suspensionInfo.suspended_by_admin
                                ? "order-alert-suspended-admin"
                                : "order-alert-suspended"
                            }`}
                          >
                            <span className="order-alert-icon">
                              <HiExclamation />
                            </span>
                            <div className="order-alert-content">
                              <p className="order-alert-title">
                                {order.config?.storage_tier || order.config?.acl
                                  ? "Space Suspended"
                                  : "Droplet Suspended"}
                              </p>
                              <p className="order-alert-desc">
                                {order.suspensionInfo.suspended_reason ||
                                  (order.config?.storage_tier ||
                                  order.config?.acl
                                    ? "Space is suspended"
                                    : "Droplet is suspended")}
                                {order.suspensionInfo.suspension_type ===
                                  "overdue" &&
                                  " - Please pay the renewal invoice to unsuspend"}
                                {order.suspensionInfo.suspended_by_admin &&
                                  " - Contact support to unsuspend"}
                              </p>
                            </div>
                          </div>
                        )}

                      {/* Renewal Invoice Alert */}
                      {order.renewalInvoiceId &&
                        order.status === "activated" && (
                          <div className="order-alert order-alert-renewal">
                            <span className="order-alert-icon">
                              <HiDocumentText />
                            </span>
                            <div className="order-alert-content">
                              <p className="order-alert-title">
                                Renewal Invoice #
                                {order.renewalInvoiceNumber ||
                                  order.renewalInvoiceId}
                              </p>
                              {order.renewalPaymentUrl && (
                                <a
                                  href={order.renewalPaymentUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="order-alert-link"
                                >
                                  <>
                                    Pay Renewal Invoice
                                    <HiArrowNarrowRight className="order-alert-link-icon" />
                                  </>
                                </a>
                              )}
                            </div>
                          </div>
                        )}
                    </div>
                  )}

                  {/* Actions Footer */}
                  <div className="order-footer">
                    <div className="order-actions">
                      {order.status === "pending" && order.paymentUrl && (
                        <div className="orders-paybox">
                          <button
                            type="button"
                            className="btn-primary cta-light"
                            onClick={() => {
                              api
                                .post("/payments/create", {
                                  orderId: order.id,
                                  provider: getPaymentProvider(order.id),
                                })
                                .then((res) => {
                                  const urlToOpen = res.data?.url;
                                  if (urlToOpen) {
                                    window.open(urlToOpen, "_blank");
                                  } else {
                                    toast.error(
                                      "Payment link not available. Please try again."
                                    );
                                  }
                                })
                                .catch((err) => {
                                  toast.error(
                                    err.response?.data?.message ||
                                      "Failed to start payment"
                                  );
                                });
                            }}
                          >
                            Pay Now
                          </button>
                          <PaymentModeDropdown
                            value={getPaymentProvider(order.id)}
                            onChange={(next) =>
                              setPaymentProviderByOrderId((prev) => ({
                                ...prev,
                                [order.id]: next,
                              }))
                            }
                          />
                        </div>
                      )}
                      {order.dropletId && (
                        <button
                          className="btn-primary btn-sm"
                          onClick={() => navigate(`/server/${order.dropletId}`)}
                        >
                          View Droplet
                        </button>
                      )}
                      {isSpacesOrder &&
                        (order.resourceId || order.config?.name) &&
                        order.status === "activated" && (
                          <button
                            className="btn-primary btn-sm"
                            onClick={() => {
                              navigate(
                                `/spaces/${order.resourceId || order.config?.name}`
                              );
                            }}
                          >
                            View Space
                          </button>
                        )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default PaymentStatus;
